/**
 * Selection rules for the Inventory category picker. A parent category
 * stands for all of its subcategories; ticking every subcategory collapses
 * back to the parent, and unticking one under a selected parent expands the
 * parent into the other subcategories.
 */
import { UNCATEGORISED } from './inventoryFilters';

export type CategorySelection = {
  categories: string[];
  subCategories: string[];
};

export type CategoryNode = {
  /** Empty for products with no category. */
  category: string;
  subCategories: { name: string }[];
};

export type CategoryState = 'on' | 'off' | 'mixed';

/** The value stored for a node: its name, or the uncategorised token. */
export const categoryKey = (node: CategoryNode): string =>
  node.category || UNCATEGORISED;

export const getCategoryState = (
  selection: CategorySelection,
  node: CategoryNode,
): CategoryState => {
  if (selection.categories.includes(categoryKey(node))) return 'on';
  const anySub = node.subCategories.some(sub =>
    selection.subCategories.includes(sub.name),
  );
  return anySub ? 'mixed' : 'off';
};

export const isSubCategorySelected = (
  selection: CategorySelection,
  node: CategoryNode,
  subCategory: string,
): boolean =>
  selection.categories.includes(categoryKey(node)) ||
  selection.subCategories.includes(subCategory);

const without = (list: string[], items: string[]) =>
  list.filter(item => !items.includes(item));

export const toggleCategoryNode = (
  selection: CategorySelection,
  node: CategoryNode,
): CategorySelection => {
  const key = categoryKey(node);
  const subs = node.subCategories.map(sub => sub.name);
  if (selection.categories.includes(key)) {
    return {
      categories: selection.categories.filter(c => c !== key),
      subCategories: without(selection.subCategories, subs),
    };
  }
  return {
    categories: [...selection.categories, key],
    subCategories: without(selection.subCategories, subs),
  };
};

export const toggleSubCategory = (
  selection: CategorySelection,
  node: CategoryNode,
  subCategory: string,
): CategorySelection => {
  const key = categoryKey(node);
  const subs = node.subCategories.map(sub => sub.name);
  if (selection.categories.includes(key)) {
    // Parent covered every subcategory; keep the others, drop this one.
    return {
      categories: selection.categories.filter(c => c !== key),
      subCategories: [
        ...without(selection.subCategories, subs),
        ...subs.filter(sub => sub !== subCategory),
      ],
    };
  }
  if (selection.subCategories.includes(subCategory)) {
    return {
      ...selection,
      subCategories: selection.subCategories.filter(s => s !== subCategory),
    };
  }
  const next = [...selection.subCategories, subCategory];
  if (subs.length && subs.every(sub => next.includes(sub))) {
    return {
      categories: [...selection.categories, key],
      subCategories: without(next, subs),
    };
  }
  return { ...selection, subCategories: next };
};

export const countSelectedCategories = (selection: CategorySelection): number =>
  selection.categories.length + selection.subCategories.length;
